import { useEffect } from 'react';

import styled from '@emotion/styled';
import { Error } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';

import { isPublicRoute as determineIsPublicRoute } from '@/utils/api';
import { isExternalIFrame } from '@/utils/iframe';

import { SegmentationsHatchPatterns } from '../../../../common-components/elements/icon/SegmentationsHatchPatterns';
import { useGetImageWithSegmentations } from '../../../../react-query/useSegmentations';
import { annotationStyles } from '../../../../theme/annotationStyles';
import { useCornerstone } from '../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../utils/cornerstone/useDicomCanvas';
import { toothPartsTogglesAtom } from '../../findingSidebar/atoms';
import {
  isHQEnabledAtom,
  previewDisplayDeviceAtom,
  showSegmentationAtom,
} from '../../multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../../types';
import { selectedTeethPreviewAtom } from '../atoms';

const ImageContainer = styled.div`
  height: 25em;
  width: 30em;
  & .cornerstoneElement {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-bottom: 0em;
    height: 100%;
    width: 100%;
    pointer-events: none;
    @media (max-width: 600px) {
      height: 100%;
      margin: 0;
    }
  }
  ${annotationStyles}
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
`;

export const PatientDownloadableImage = ({
  activeImage,
  isSelectedPhoto,
  position,
  estimatedVisitId,
}: {
  activeImage: ImageObject;
  isSelectedPhoto?: boolean;
  position?: 'left' | 'right';
  estimatedVisitId?: number;
}) => {
  const isImageWithSegmentations =
    useGetImageWithSegmentations(
      activeImage?.imageId,
      estimatedVisitId?.toString(),
    ) && activeImage.detectedImageType !== 'COLOR_IMAGE';
  const isToothParts = useAtomValue(toothPartsTogglesAtom).tooth_anatomy;
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const isHQEnabled = useAtomValue(isHQEnabledAtom);
  const [dicomCanvasRef, setDicomCanvasRef] = useDicomCanvas();
  const selectedTeethPreview = useAtomValue(selectedTeethPreviewAtom);
  const previewDisplayDevice = useAtomValue(previewDisplayDeviceAtom);

  const isPublicRoute = determineIsPublicRoute() || isExternalIFrame();

  // Create unique viewport ID for compare mode
  const viewportId = isSelectedPhoto
    ? `case-presentation-download-selected-photo-${activeImage.imageId}`
    : position
    ? `case-presentation-download-${position}-${activeImage.imageId}`
    : `case-presentation-download-${activeImage.imageId}`;

  const { isCornerstoneReady, loadImageFileIntoCanvas, imageLoadStatus } =
    useCornerstone({
      isThumbnail: false,
      dicomCanvasRef,
      activeImage: activeImage,
      viewportId: viewportId,
      source: 'case-presentation-download',
      selectedTeeth: selectedTeethPreview,
      visitId: estimatedVisitId?.toString(),
    });

  useEffect(() => {
    if (isCornerstoneReady && activeImage && dicomCanvasRef) {
      loadImageFileIntoCanvas(isHQEnabled);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'loadImageFileIntoCanvas'. Either include it or remove the dependency array. */
  }, [activeImage, dicomCanvasRef, isCornerstoneReady]);

  const cornerstoneClasses = classNames({
    ['cornerstoneElement']: true,
    'patient-view': showSegmentation && isImageWithSegmentations,
    'tooth-parts': showSegmentation && isImageWithSegmentations && isToothParts,
    'patient-download': true,
    'mobile-preview': previewDisplayDevice === 'mobile',
  });

  return (
    <ImageContainer className="cornerstoneElement">
      {/* need this here because the hatch patterns are not being applied to the image for qr code version. the default version already has this in another place */}
      {isPublicRoute && <SegmentationsHatchPatterns />}
      {imageLoadStatus === 'error' && <Error color="error" />}
      <div className={cornerstoneClasses} ref={setDicomCanvasRef} />
      {imageLoadStatus === 'loading' && (
        <LoadingSpinner>
          <CircularProgress />
        </LoadingSpinner>
      )}
    </ImageContainer>
  );
};
